import React, { useState } from 'react'
import pluralize from 'pluralize'
import { Drawer } from 'antd'
import { useHistory, useParams } from 'react-router-dom'

import { useToggle, usePet, stringifyDate, server } from '../../modules'

import Receive from '../reusable/receive'
import ImagePreview from '../reusable/imagePreview'
import { groupFieldsIntoRows } from '../reusable/inputs'

const Thumbnail = ({ preview, directory, img }) => (
    <div onClick={ () => preview(img) } style={{ backgroundImage: `url(${server}/uploads/${directory}/${img})` }} className="flex flex-col justify-between shadow-lg h-56 bg-cover rounded-sm border mb-3 border-gray-200 cursor-pointer">
        <span className="bg-gray-0 opacity-0 transition-opacity text-white flex items-center justify-center w-full h-full hover:bg-gray-500 hover:opacity-40">View</span>
    </div>
)

export const SingleResult = ({ entry, field }) => {
    const [previewVisible, setPreview] = useState(false)
    const [previewImage, setPreviewImage] = useState()

    const preview = async img => {
        setPreview(true)
        setPreviewImage(`${server}/uploads/${directory}/${img}`)
    }

    const handleCancel = () => setPreview(false)
    
    let currentVal = entry?.fields?.find(fld => fld.name === field.name)?.value
    let curr = entry?.fields?.find(fld => fld.name === field.name)
    let template = entry?.item?.fields?.find(fld => fld.name === field.name)
    let directory = pluralize(entry?.item?.name ?? entry?.ref?.name)?.toLowerCase()

    let prev = template?.type === 'SearchableSelect' || template?.type === 'Select' ? { value: currentVal } : template?.type === 'Date' ? currentVal ? currentVal[0] : undefined : currentVal

    return (
        <>
            <ImagePreview
                visible={ previewVisible }
                image={ previewImage }
                toggle={ handleCancel } />
            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                {field.name}
            </label>
            {
                prev?.length > 0 ?
                    field.type === 'Textarea' || field.type === 'Text' ? <p style={{ whiteSpace: 'pre' }}>{ prev }</p>
                    : field.type === 'Number' ? <p>{ Number(prev[0] ?? 0) } { template?.unit }</p>
                    : field.type === 'Date' ? <p>{ prev ? stringifyDate(prev, true) : '' }</p>
                    : field.type === 'Select' || field.type === 'SearchableSelect' || field.type === 'CreatableSelect' ? prev[0]
                    : field.type === 'ImageUpload' ? 
                        <Thumbnail
                            directory={ directory }
                            preview={ preview }
                            img={ prev[0] } />
                    : field.type === 'MultiSearchableSelect' ? prev.join(", ")
                    : field.type === 'MultiImageUpload' ?
                        <div className="grid gap-3 md:grid-cols-3 sm:grid-cols-2 lg:grid-cols-4">
                            {
                                prev?.map((img, i) =>
                                    <Thumbnail
                                        key={ i }
                                        directory={ directory }
                                        preview={ preview }
                                        img={ img } /> )
                            }            
                        </div>
                    : field.type === 'IncrementalTextarea' ? 
                        <>
                            <p style={{ whiteSpace: 'pre' }}>{ prev }</p>
                            {
                                curr &&
                                <p className="text-gray-500 mt-0 text-xs italic">Filled by { curr?.filledBy?.name } on { stringifyDate(curr.filledOn) }</p>
                            }
                        </>
                    : prev?.map(prev => prev)
                : ''
            }
        </>
    )
}

// Read-only counterpart to EntryFields (components/reusable/inputs.js) - same
// groupFieldsIntoRows grouping, applied to SingleResult instead of the editable EntryField.
const SingleResults = ({ entry, fields }) =>
    groupFieldsIntoRows(fields).map(row =>
        row.fields.length > 1 ?
            <div className="w-full mb-3">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                    { row.group }
                </label>
                <div className="flex flex-wrap gap-6">
                    {
                        row.fields.map(field =>
                            <div className="flex-1 min-w-0">
                                <SingleResult entry={ entry } field={ field } />
                            </div>)
                    }
                </div>
            </div>
        :
            <SingleResult entry={ entry } field={ row.fields[0] } />
    )

const EntrySummary = ({ path }) => {
    const [oi, setOi] = useState()

    const history = useHistory()
    const { ref } = useParams()
    
    const [visible, toggleVisible] = useToggle(true)

    const [{ loading, error }] = usePet('entriesFromOrderItem', { ref }, { notify: true, callback: ({ data }) => setOi(data) })
    
    const close = () => {
        toggleVisible()
        setTimeout(() => history.goBack(), 300)
    }

    const print = () => {
        if (oi?.entries)
            for (let entry of oi.entries)
                window.open(`${server}/entry/${entry?._id}/print`, '_blank').focus()
    }

    return (
        <Drawer
            closable
            maskClosable
            visible={visible}
            onClose={close}
            footer={
                <div className="w-full text-right">
                    <button onClick={ print } className="text-white mr-2 hisani-btn-orange font-medium rounded-lg text-sm px-5 py-2.5 text-center">Print</button>
                </div>
            }
            width="50%">
            <p className="font-extrabold text-3xl mb-0">{ oi?.ref?.name ?? oi?.name ?? 'Summary' }</p>
            <p className="text-gray-500 text-xs italic mb-0">{ oi?.patient?.name ?? 'Patient'}'s visit { oi?.date && `on ${stringifyDate(oi.date)}`}</p>
            { oi?.seen && <p className="text-gray-500 mt-0 text-xs italic">Seen by { oi?.seenBy?.name } on { stringifyDate(oi.seenOn) }</p> }
            <Receive
                progress
                loading={loading}
                error={error}>
                {
                    oi ?
                        oi?.entries.length > 0 ?
                            oi.entries.map(entry =>
                                <div className="mb-1 border-b-0 border-gray-500">
                                    {
                                        (entry?.diagnoses?.length ?? 0) > 0 &&
                                        <div className="mb-3">
                                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                                Diagnoses
                                            </label>
                                            {
                                                entry.diagnoses.map((diagnosis, index) =>
                                                    <p key={ `${diagnosis?.code}-${index}` } className="mb-1">
                                                        { diagnosis?.role === 'primary' ? 'Primary' : 'Secondary' }: { diagnosis?.code } - { diagnosis?.title }
                                                    </p>)
                                            }
                                        </div>
                                    }
                                    {
                                        <SingleResults
                                            entry={ entry }
                                            fields={ entry?.item?.fields ?? [] } />
                                    }
                                    {
                                        (entry?.followUpAppointments?.length ?? 0) > 0 &&
                                        <div className="mb-3">
                                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                                Follow Up Appointments
                                            </label>
                                            {
                                                entry.followUpAppointments.map((followUp, index) =>
                                                    <p key={ followUp?._id ?? index } className="mb-1">
                                                        { followUp?.consultationType?.name ?? 'Follow Up' } on { stringifyDate(followUp?.scheduledFor, true) }
                                                    </p>)
                                            }
                                        </div>
                                    }
                                    { entry.filled && <p className="text-gray-500 mt-0 text-xs italic">Recorded by { entry?.filledBy?.name } on { stringifyDate(entry?.filledOn) }</p> }
                                </div>
                            )
                        : 
                            <div className="w-full h-16 mt-4 flex items-center justify-center">
                                <p className="font-semibold text-base">Nothing has been recorded yet</p>
                            </div>
                    : null
                }
            </Receive>
        </Drawer>
    )
}

export default EntrySummary
